/**
 * The plan's working grid and everything it opens — the Suggested Resource picker, Line
 * Shares, Candidates, line notes, the rate editor, the line / Workstream dialogs, sizing and
 * the Resource profile drawer. Owns the saves the grid makes, chained so they land in order.
 *
 * Shared by the plan page (the full editor) and the Staffing Requests page, where
 * `staffingOnly` keeps just the staffing half live: who is on each line, its Open / Proposed
 * / Confirmed status, Line Shares, Candidates and notes. The plan's content — line hours,
 * rates, lines, Workstreams, sizing and the ramp — is locked there; it is edited on the plan.
 */

import { useRef, useState } from "react";
import { toast } from "sonner";
import { ResourceDetailDrawer } from "@/components/dashboard/ResourceDetailDrawer";
import { PlanGrid } from "@/components/plans/PlanGrid";
import { PlanLineCandidatesPopover } from "@/components/plans/PlanLineCandidatesPopover";
import { PlanLineNotesDrawer } from "@/components/plans/PlanLineNotesDrawer";
import { PlanLineRateEditor } from "@/components/plans/PlanLineRateEditor";
import { PlanLineDialog, WorkstreamDialog } from "@/components/plans/PlanPanels";
import { SuggestedResourcePicker } from "@/components/plans/SuggestedResourcePicker";
import { WorkstreamSizingDialog } from "@/components/plans/WorkstreamSizingDialog";
import { useSetPlanRamp } from "@/hooks/use-plan-composition";
import {
  useAddPlanLineCandidate,
  useCreatePlanLine,
  useSaveGrid,
  useSetPlanLineShares,
  useSetSuggestedResource,
} from "@/hooks/use-resource-plans";
import { sharesAfterAdding } from "@/lib/plan-line-share-edits";
import { planLineStatusWrite } from "@/lib/plan-line-status";
import type {
  PlanLineView,
  ResourcePlanDetailView,
  WorkstreamView,
} from "@/schemas/resource-plans";

interface Props {
  plan: ResourcePlanDetailView;
  /** Lock the plan's content and keep only staffing live (the Staffing Requests page). */
  staffingOnly?: boolean;
}

export function PlanWorkingGrid({ plan, staffingOnly = false }: Props) {
  const saveGrid = useSaveGrid(plan.id);
  const setSuggested = useSetSuggestedResource(plan.id);
  const setShares = useSetPlanLineShares();
  // RP-24: a grid save sends line hours first and the touched lines' Share sets after, so a
  // Share set is always checked against the line hours saved alongside it.
  const lastGridSave = useRef<Promise<unknown>>(Promise.resolve());
  const createLine = useCreatePlanLine(plan.id);
  const setRamp = useSetPlanRamp();

  const [lineDialog, setLineDialog] = useState<
    { open: false } | { open: true; line: PlanLineView | null; workstreamId: string | null }
  >({ open: false });
  const [workstreamDialog, setWorkstreamDialog] = useState<
    { open: false } | { open: true; workstream: WorkstreamView | null }
  >({ open: false });
  // The Suggested Resource picker is an inline popover anchored to the Who cell, not a
  // modal — a 48px chip click should not take over the screen.
  // `share` mode (RP-24) adds the picked Resource as a Line Share instead of naming them.
  const [who, setWho] = useState<{
    line: PlanLineView;
    anchor: HTMLElement;
    mode?: "suggest" | "share";
  } | null>(null);
  // RP-32.2 — the shortlist popover, and the picker reused to add to it.
  const [shortlist, setShortlist] = useState<{ line: PlanLineView; anchor: HTMLElement } | null>(
    null,
  );
  const [addingCandidate, setAddingCandidate] = useState<{
    line: PlanLineView;
    anchor: HTMLElement;
    excludeIds: ReadonlySet<string>;
  } | null>(null);
  const addCandidate = useAddPlanLineCandidate();
  // RP-32.4 — the line whose notes drawer is open.
  const [notesLineId, setNotesLineId] = useState<string | null>(null);
  const notesLine = notesLineId === null ? null : plan.lines.find((l) => l.id === notesLineId);
  // The grid's Rate cell opens the inline rate editor beside it.
  const [rateEdit, setRateEdit] = useState<{ line: PlanLineView; anchor: HTMLElement } | null>(
    null,
  );
  // RP-3.2 / RP-3.3 — sizing and the mix, opened from a Workstream's group header.
  const [sizing, setSizing] = useState<WorkstreamView | null>(null);
  // The grid's eye icon opens this Resource's profile drawer over the plan.
  const [profileResourceId, setProfileResourceId] = useState<string | null>(null);

  return (
    <>
      <PlanGrid
        plan={plan}
        staffingView={staffingOnly}
        saving={saveGrid.isPending || setShares.isPending}
        /*
         * RP-24: every grid save is chained behind the previous one — line hours, then
         * the touched lines' Share sets — so a Share set is always checked against the
         * hours saved before it, and two quick saves can never land out of order.
         */
        onSaveCells={(cells) => {
          const prior = lastGridSave.current;
          lastGridSave.current = prior.then(() =>
            saveGrid.mutateAsync({ cells }).catch(() => undefined),
          );
        }}
        onSaveRunRates={(runRates) => {
          const prior = lastGridSave.current;
          lastGridSave.current = prior.then(() =>
            saveGrid.mutateAsync({ runRates }).catch(() => undefined),
          );
        }}
        onSaveShares={(sets) => {
          const prior = lastGridSave.current;
          const chain = prior.then(async () => {
            let allSaved = true;
            for (const set of sets) {
              const ok = await setShares
                .mutateAsync({
                  lineId: set.planLineId,
                  shares: set.shares,
                  message: set.message,
                })
                .then(() => true)
                .catch(() => false);
              if (!ok) allSaved = false;
            }
            return allSaved;
          });
          lastGridSave.current = chain;
          return chain;
        }}
        onAddShareResource={(line, anchor) => setWho({ line, anchor, mode: "share" })}
        addingLine={createLine.isPending}
        onAddLine={(input) => createLine.mutate(input)}
        onAddWorkstream={() => setWorkstreamDialog({ open: true, workstream: null })}
        onOpenLine={(line) => {
          if (!staffingOnly) setLineDialog({ open: true, line, workstreamId: line.workstreamId });
        }}
        onOpenWorkstream={(workstream) => {
          if (!staffingOnly) setWorkstreamDialog({ open: true, workstream });
        }}
        onOpenWho={(line, anchor) => setWho({ line, anchor })}
        onEditRate={(line, anchor) => {
          if (!staffingOnly) setRateEdit({ line, anchor });
        }}
        onSizeWorkstream={staffingOnly ? undefined : (workstream) => setSizing(workstream)}
        onSetRamp={
          staffingOnly ? undefined : (months) => setRamp.mutate({ planId: plan.id, months })
        }
        onConfirmWho={(line) =>
          setSuggested.mutate({
            lineId: line.id,
            resourceId: line.suggestedResourceId,
            confirmed: true,
          })
        }
        onSetLineStatus={(line, status, anchor) => {
          const write = planLineStatusWrite(line, status);
          if (write === null) return;
          // Proposed / Confirmed need a person: an Open line goes to the picker, and
          // whoever is picked lands as Proposed.
          if (write === "pick") {
            setWho({ line, anchor });
            return;
          }
          setSuggested.mutate({ lineId: line.id, ...write });
        }}
        onViewResource={(resourceId) => setProfileResourceId(resourceId)}
        onOpenCandidates={(line, anchor) => setShortlist({ line, anchor })}
        onOpenNotes={(line) => setNotesLineId(line.id)}
      />
      {sizing !== null && (
        <WorkstreamSizingDialog
          plan={plan}
          workstream={sizing}
          open
          onOpenChange={(open) => !open && setSizing(null)}
        />
      )}

      {lineDialog.open && (
        <PlanLineDialog
          plan={plan}
          line={lineDialog.line}
          workstreamId={lineDialog.workstreamId}
          open
          onOpenChange={(open) => !open && setLineDialog({ open: false })}
        />
      )}
      {workstreamDialog.open && (
        <WorkstreamDialog
          planId={plan.id}
          workstream={workstreamDialog.workstream}
          open
          onOpenChange={(open) => !open && setWorkstreamDialog({ open: false })}
        />
      )}
      {who !== null && (
        <SuggestedResourcePicker
          // Remount per line, so the picker needs no reset effect: a fresh mount starts
          // with an empty search and a focused input by construction.
          key={who.line.id}
          planId={plan.id}
          // The live line, so a saved reason shows without closing the picker.
          line={plan.lines.find((l) => l.id === who.line.id) ?? who.line}
          anchor={who.anchor}
          pending={setSuggested.isPending || setShares.isPending}
          onClose={() => setWho(null)}
          onPick={(resourceId, confirmed) => {
            if (who.mode === "share") {
              if (resourceId === null) {
                setWho(null);
                return;
              }
              const next = sharesAfterAdding(
                who.line,
                resourceId,
                plan.entryMode === "SteadyState",
              );
              if (next === null) {
                toast.info("That Resource is already on this line.");
                return;
              }
              setShares.mutate(
                {
                  lineId: who.line.id,
                  shares: next,
                  message: `Line split across ${next.length} Resources — give each their hours`,
                },
                { onSuccess: () => setWho(null) },
              );
              return;
            }
            setSuggested.mutate(
              { lineId: who.line.id, resourceId, confirmed },
              { onSuccess: () => setWho(null) },
            );
          }}
          onSetReason={(reason, reasonNote) => {
            const live = plan.lines.find((l) => l.id === who.line.id) ?? who.line;
            setSuggested.mutate({
              lineId: live.id,
              resourceId: live.suggestedResourceId,
              confirmed: live.suggestedResourceConfirmed,
              reason,
              reasonNote,
            });
          }}
        />
      )}
      {notesLine != null && (
        <PlanLineNotesDrawer
          key={notesLine.id}
          planId={plan.id}
          line={notesLine}
          onClose={() => setNotesLineId(null)}
        />
      )}
      {shortlist !== null && (
        <PlanLineCandidatesPopover
          key={shortlist.line.id}
          line={plan.lines.find((l) => l.id === shortlist.line.id) ?? shortlist.line}
          anchor={shortlist.anchor}
          onClose={() => setShortlist(null)}
          onAdd={(excludeIds) => {
            setAddingCandidate({ ...shortlist, excludeIds });
            setShortlist(null);
          }}
        />
      )}
      {addingCandidate !== null && (
        <SuggestedResourcePicker
          key={`candidate-${addingCandidate.line.id}`}
          mode="candidate"
          planId={plan.id}
          line={addingCandidate.line}
          anchor={addingCandidate.anchor}
          excludeIds={addingCandidate.excludeIds}
          pending={addCandidate.isPending}
          onClose={() => setAddingCandidate(null)}
          onPick={(resourceId) => {
            if (resourceId === null) return;
            const back = addingCandidate;
            addCandidate.mutate(
              { lineId: back.line.id, resourceId },
              {
                onSuccess: () => {
                  setAddingCandidate(null);
                  setShortlist({ line: back.line, anchor: back.anchor });
                },
              },
            );
          }}
        />
      )}
      {rateEdit !== null && (
        <PlanLineRateEditor
          // Remount per line, so the editor starts from that line's rate by construction.
          key={rateEdit.line.id}
          plan={plan}
          line={rateEdit.line}
          anchor={rateEdit.anchor}
          onClose={() => setRateEdit(null)}
        />
      )}
      {profileResourceId !== null && (
        <ResourceDetailDrawer id={profileResourceId} onClose={() => setProfileResourceId(null)} />
      )}
      {profileResourceId !== null && (
        <ResourceDetailDrawer id={profileResourceId} onClose={() => setProfileResourceId(null)} />
      )}
    </>
  );
}
